import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getSupplier, 
  patchSupplier, 
  addProductToSupplier,
  removeProductFromSupplier,
  addSupplierProductCost,
  getSupplierProductCostHistory 
} from '../../collections/suppliers';
import { getProducts } from '../../collections/products';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
  Breadcrumbs,
  Link,
  Avatar,
  Stack,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Collapse,
  InputAdornment,
  Autocomplete,
  Tooltip,
  Alert,
  Skeleton,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import UpdateIcon from '@mui/icons-material/Update';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SupplierFinanceTab from './SupplierFinanceTab';

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forceRerender, setForceRerender] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Product management states
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addingProduct, setAddingProduct] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Cost management states
  const [expandedProducts, setExpandedProducts] = useState({});
  const [editingCosts, setEditingCosts] = useState({});
  const [costHistories, setCostHistories] = useState({});
  const [loadingCosts, setLoadingCosts] = useState({});
  
  // Statistics
  const [statistics, setStatistics] = useState({
    totalProducts: 0,
    averageCostTrend: 'stable',
    lastUpdateDate: null,
    recentCostChanges: 0
  });

  useEffect(() => {
    fetchSupplierData();
    fetchAllProducts();
  }, [id, forceRerender]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      const supplierData = await getSupplier(id);
      console.log('Supplier data received:', supplierData);
      console.log('Products structure:', supplierData.products);
      setSupplier(supplierData);
      
      // Initialize cost histories from supplier product data
      if (supplierData.products?.items) {
        const initialCostHistories = {};
        supplierData.products.items.forEach(supplierProduct => {
          if (supplierProduct.cost_history && supplierProduct.cost_history.length > 0) {
            initialCostHistories[supplierProduct.id] = supplierProduct.cost_history;
          }
        });
        setCostHistories(initialCostHistories);
        
        // Calculate statistics
        setStatistics({
          totalProducts: supplierData.products.items.length,
          averageCostTrend: 'stable', // This would need real calculation
          lastUpdateDate: new Date().toLocaleDateString('vi-VN'),
          recentCostChanges: 0
        });
      }
    } catch (err) {
      setError(err.message || 'Error fetching supplier details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await getProducts({ per_page: 9999, status: 'Active' });
      setAllProducts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchCostHistory = async (supplierProductId) => {
    // If we already have fetched history or if the supplier product already includes cost history, don't fetch again
    if (costHistories[supplierProductId]) return;
    
    // Check if the supplier product already has cost_history from initial load
    const supplierProduct = supplier?.products?.items?.find(sp => sp.id === supplierProductId);
    if (supplierProduct?.cost_history?.length > 0) {
      setCostHistories(prev => ({ ...prev, [supplierProductId]: supplierProduct.cost_history }));
      return;
    }
    
    setLoadingCosts(prev => ({ ...prev, [supplierProductId]: true }));
    try {
      const response = await getSupplierProductCostHistory(supplierProductId);
      setCostHistories(prev => ({ ...prev, [supplierProductId]: response.data || [] }));
    } catch (error) {
      console.error('Failed to fetch cost history:', error);
    } finally {
      setLoadingCosts(prev => ({ ...prev, [supplierProductId]: false }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEdit = () => {
    navigate(`/suppliers/${id}/edit`);
  };

  const handleStatusChange = async () => {
    const newStatus = supplier.status === "Active" ? "Inactive" : "Active";
    const confirmAction = window.confirm(
      `Bạn có chắc muốn ${newStatus === 'Active' ? 'kích hoạt' : 'vô hiệu hóa'} nhà cung cấp này không?`
    );

    if (confirmAction) {
      try {
        await patchSupplier(id, { status: newStatus });
        setForceRerender(prev => !prev);
      } catch (error) {
        console.error("Failed to update status", error);
        setError("Failed to update status. Please try again.");
      }
    }
  };

  const handleAddProduct = async () => {
    if (!selectedProduct) return;
    
    setAddingProduct(true);
    try {
      await addProductToSupplier(supplier.id, selectedProduct.id);
      setSelectedProduct(null);
      setForceRerender(prev => !prev);
    } catch (error) {
      console.error('Failed to add product:', error);
    } finally {
      setAddingProduct(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    if (window.confirm('Bạn có chắc muốn xóa sản phẩm này?')) {
      try {
        await removeProductFromSupplier(supplier.id, productId);
        setForceRerender(prev => !prev);
      } catch (error) {
        console.error('Failed to remove product:', error);
      }
    }
  };

  const toggleProductExpansion = (supplierProductId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [supplierProductId]: !prev[supplierProductId]
    }));
    
    if (!expandedProducts[supplierProductId]) {
      fetchCostHistory(supplierProductId);
    }
  };

  const startEditingCost = (supplierProductId) => {
    setEditingCosts(prev => ({
      ...prev,
      [supplierProductId]: {
        cost: '',
        effectiveDate: dayjs()
      }
    }));
  };

  const cancelEditingCost = (supplierProductId) => {
    setEditingCosts(prev => {
      const newState = { ...prev };
      delete newState[supplierProductId];
      return newState;
    });
  };

  const saveCost = async (supplierProductId) => {
    const costData = editingCosts[supplierProductId];
    if (!costData || !costData.cost) return;

    try {
      const response = await addSupplierProductCost(supplierProductId, {
        cost: parseFloat(costData.cost),
        effective_date: costData.effectiveDate.format('YYYY-MM-DD')
      });
      
      // Add the new cost to the existing cost history
      const newCost = response.data;
      setCostHistories(prev => {
        const currentHistory = prev[supplierProductId] || [];
        // Insert the new cost at the beginning (most recent) and sort properly
        const updatedHistory = [newCost, ...currentHistory].sort((a, b) => {
          // First sort by effective_date descending
          const dateComparison = new Date(b.effective_date) - new Date(a.effective_date);
          if (dateComparison !== 0) return dateComparison;
          // If dates are same, sort by id descending (bigger id first)
          return b.id - a.id;
        });
        
        return {
          ...prev,
          [supplierProductId]: updatedHistory
        };
      });
      
      cancelEditingCost(supplierProductId);
    } catch (error) {
      console.error('Failed to save cost:', error);
    }
  };

  const updateCostData = React.useCallback((supplierProductId, field, value) => {
    setEditingCosts(prev => {
      const currentCost = prev[supplierProductId] || {};
      if (currentCost[field] === value) {
        return prev; // No change, prevent re-render
      }
      return {
        ...prev,
        [supplierProductId]: {
          ...currentCost,
          [field]: value
        }
      };
    });
  }, []);

  const InfoItem = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, borderRadius: '8px', backgroundColor: 'rgba(14, 42, 4, 0.02)', border: '1px solid rgba(14, 42, 4, 0.1)' }}>
      <Box sx={{ mr: 3, color: '#0e2a04', p: 1, borderRadius: '8px', backgroundColor: 'rgba(14, 42, 4, 0.1)' }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block',
            color: '#666666',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem',
            mb: 0.5
          }}
        >
          {label}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: value ? 500 : 300, 
            color: value ? '#0F172A' : '#64748B',
            fontSize: '1rem'
          }}
        >
          {value || 'Chưa có thông tin'}
        </Typography>
      </Box>
    </Box>
  );

  const StatCard = ({ title, value, icon, trend }) => (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              color="#666666" 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.75rem',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                mt: 1,
                fontWeight: 700,
                color: '#0e2a04',
                fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              {value}
            </Typography>
          </Box>
          <Avatar 
            sx={{ 
              background: 'linear-gradient(135deg, #0e2a04 0%, #1a4f0a 100%)',
              width: 56,
              height: 56,
              boxShadow: '0 2px 4px rgba(0, 208, 132, 0.2)'
            }}
          >
            {icon}
          </Avatar>
        </Box>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            {trend === 'up' ? <TrendingUpIcon sx={{ color: '#0e2a04' }} /> : <TrendingDownIcon sx={{ color: '#d4a574' }} />}
            <Typography 
              variant="body2" 
              sx={{ 
                ml: 1, 
                fontWeight: 600,
                color: trend === 'up' ? '#00B366' : '#d4a574',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif"
              }}
            >
              {trend === 'up' ? 'Tăng' : 'Giảm'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const ProductCostRow = React.memo(({ supplierProduct, product }) => {
    // Safety check for required props
    if (!supplierProduct || !product) {
      console.error('ProductCostRow: Missing required props', { supplierProduct, product });
      return null;
    }
    
    const isExpanded = expandedProducts[supplierProduct.id];
    const isEditing = editingCosts[supplierProduct.id];
    
    // Use cost_history from supplier product if available, otherwise use fetched history
    const costHistory = costHistories[supplierProduct.id] || supplierProduct.cost_history || [];
    const isLoadingHistory = loadingCosts[supplierProduct.id];
    
    // Get latest cost from history
    const latestCost = costHistory.length > 0 ? costHistory[0] : null;
    const previousCost = costHistory.length > 1 ? costHistory[1] : null;
    const costTrend = latestCost && previousCost 
      ? (latestCost.cost > previousCost.cost ? 'up' : 'down')
      : null;

    return (
      <>
        <TableRow>
          <TableCell>{product.id}</TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {product.name}
            </Typography>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {latestCost ? `${latestCost.cost.toLocaleString('vi-VN')}đ` : 'Chưa có giá'}
              </Typography>
              {costTrend && (
                <Tooltip title={costTrend === 'up' ? 'Tăng giá' : 'Giảm giá'}>
                  {costTrend === 'up' 
                    ? <TrendingUpIcon sx={{ ml: 1, fontSize: 16, color: 'error.main' }} />
                    : <TrendingDownIcon sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />
                  }
                </Tooltip>
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {latestCost 
                ? new Date(latestCost.effective_date).toLocaleDateString('vi-VN')
                : '-'
              }
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {!isEditing ? (
                <>
                  <Tooltip title="Cập nhật giá">
                    <IconButton 
                      size="small" 
                      onClick={() => startEditingCost(supplierProduct.id)}
                      sx={{
                        color: '#0e2a04',
                        backgroundColor: 'rgba(14, 42, 4, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(14, 42, 4, 0.2)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <UpdateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Xem lịch sử giá">
                    <IconButton 
                      size="small" 
                      onClick={() => toggleProductExpansion(supplierProduct.id)}
                      sx={{
                        color: '#6b8e4e',
                        backgroundColor: 'rgba(107, 142, 78, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(107, 142, 78, 0.2)',
                          color: '#1a4f0a'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Xóa sản phẩm">
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveProduct(product.id)}
                      sx={{
                        color: '#d4a574',
                        backgroundColor: 'rgba(212, 165, 116, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(212, 165, 116, 0.2)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Lưu">
                    <IconButton 
                      size="small" 
                      onClick={() => saveCost(supplierProduct.id)}
                      sx={{
                        color: '#0e2a04',
                        backgroundColor: 'rgba(14, 42, 4, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(14, 42, 4, 0.2)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Hủy">
                    <IconButton 
                      size="small" 
                      onClick={() => cancelEditingCost(supplierProduct.id)}
                      sx={{
                        color: '#8b7355',
                        backgroundColor: 'rgba(139, 115, 85, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(139, 115, 85, 0.2)',
                          color: '#d4a574',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          </TableCell>
        </TableRow>
        
        {/* Inline cost update form */}
        {isEditing && (
          <TableRow>
            <TableCell colSpan={5} sx={{ py: 2, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Giá mới"
                  type="number"
                  autoFocus
                  value={editingCosts[supplierProduct.id]?.cost || ''}
                  onChange={(e) => updateCostData(supplierProduct.id, 'cost', e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      saveCost(supplierProduct.id);
                    }
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">đ</InputAdornment>,
                    inputProps: {
                      min: 0,
                      step: 1000
                    }
                  }}
                  sx={{ 
                    width: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      fontSize: '16px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      transition: 'all 0.2s ease',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                        borderWidth: '2px'
                      },
                      '&:hover fieldset': {
                        borderColor: '#6b8e4e',
                        boxShadow: '0 0 0 2px rgba(107, 142, 78, 0.1)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0e2a04',
                        boxShadow: '0 0 0 4px rgba(14, 42, 4, 0.15)',
                        outline: 'none'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#1a1a1a',
                      padding: '12px 16px',
                      '&:focus': {
                        outline: 'none'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#666666',
                      fontWeight: 500,
                      '&.Mui-focused': {
                        color: '#0e2a04'
                      }
                    }
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Ngày hiệu lực"
                    value={editingCosts[supplierProduct.id]?.effectiveDate || dayjs()}
                    onChange={(newValue) => updateCostData(supplierProduct.id, 'effectiveDate', newValue)}
                    slotProps={{ 
                      textField: { 
                        size: 'small', 
                        sx: { 
                          width: 200,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            fontSize: '16px',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                            transition: 'all 0.2s ease',
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                              borderWidth: '2px'
                            },
                            '&:hover fieldset': {
                              borderColor: '#6b8e4e',
                              boxShadow: '0 0 0 2px rgba(107, 142, 78, 0.1)'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#0e2a04',
                              boxShadow: '0 0 0 4px rgba(14, 42, 4, 0.15)',
                              outline: 'none'
                            }
                          },
                          '& .MuiInputBase-input': {
                            color: '#1a1a1a',
                            padding: '12px 16px',
                            '&:focus': {
                              outline: 'none'
                            }
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666666',
                            fontWeight: 500,
                            '&.Mui-focused': {
                              color: '#0e2a04'
                            }
                          }
                        } 
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </TableCell>
          </TableRow>
        )}
        
        {/* Cost history expansion */}
        {isExpanded && (
          <TableRow>
            <TableCell colSpan={5} sx={{ py: 0 }}>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ m: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1, fontSize: 18 }} />
                    Lịch sử giá
                  </Typography>
                  {isLoadingHistory ? (
                    <Skeleton variant="rectangular" height={100} />
                  ) : costHistory.length > 0 ? (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Ngày hiệu lực</TableCell>
                          <TableCell>Giá</TableCell>
                          <TableCell>Thay đổi</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {costHistory.map((cost, index) => {
                          const previousCost = costHistory[index + 1];
                          const change = previousCost 
                            ? ((cost.cost - previousCost.cost) / previousCost.cost * 100).toFixed(1)
                            : null;
                          
                          return (
                            <TableRow key={cost.id}>
                              <TableCell>
                                {new Date(cost.effective_date).toLocaleDateString('vi-VN')}
                              </TableCell>
                              <TableCell>{cost.cost.toLocaleString('vi-VN')}đ</TableCell>
                              <TableCell>
                                {change && (
                                  <Chip
                                    size="small"
                                    label={`${change > 0 ? '+' : ''}${change}%`}
                                    color={change > 0 ? 'error' : 'success'}
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Chưa có lịch sử giá
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!supplier) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">Không tìm thấy nhà cung cấp.</Alert>
      </Container>
    );
  }

  const availableProducts = allProducts.filter(
    product => !supplier.products?.items?.some(sp => sp.product?.id === product.id)
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        sx={{ 
          mb: 3,
          '& .MuiBreadcrumbs-ol': {
            alignItems: 'center'
          }
        }}
      >
        <Link 
          color="inherit" 
          href="/suppliers" 
          onClick={(e) => { e.preventDefault(); navigate('/suppliers'); }}
          sx={{ 
            cursor: 'pointer',
            textDecoration: 'none',
            color: '#6b8e4e',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
              color: '#0e2a04'
            }
          }}
        >
          Nhà cung cấp
        </Link>
        <Typography color="#d4a574" sx={{ fontWeight: 600 }}>Chi tiết</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 }, 
          mb: 3,
          background: 'linear-gradient(135deg, #0e2a04 0%, #1a4f0a 100%)',
          color: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(14, 42, 4, 0.3)'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 2,
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flex: 1,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Avatar 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)', 
                mr: { xs: 2, sm: 3 },
                width: { xs: 48, sm: 64 },
                height: { xs: 48, sm: 64 },
                backdropFilter: 'blur(10px)'
              }}
            >
              <BusinessIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  wordBreak: 'break-word'
                }}
              >
                {supplier.name}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mt: 1,
                gap: { xs: 1, sm: 2 }
              }}>
                <Chip
                  label={supplier.status === 'Active' ? 'Hoạt động' : 'Không hoạt động'}
                  color={supplier.status === 'Active' ? 'success' : 'default'}
                  size="medium"
                  sx={{ 
                    fontWeight: 600,
                    bgcolor: supplier.status === 'Active' 
                      ? 'rgba(255, 255, 255, 0.2)' 
                      : 'rgba(212, 165, 116, 0.9)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}
                />
                <Typography 
                  variant="body1" 
                  sx={{ 
                    opacity: 0.9,
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  ID: {supplier.id}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack 
            direction={{ xs: 'row', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'stretch', sm: 'flex-end' }
            }}
          >
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ 
                textTransform: 'none',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                flex: { xs: 1, sm: 'none' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Chỉnh sửa
            </Button>
            <Button
              variant="outlined"
              startIcon={supplier.status === 'Active' ? <ToggleOffIcon /> : <ToggleOnIcon />}
              onClick={handleStatusChange}
              sx={{ 
                textTransform: 'none',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                flex: { xs: 1, sm: 'none' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {supplier.status === 'Active' ? 'Vô hiệu hóa' : 'Kích hoạt'}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                {supplier.status === 'Active' ? 'Tắt' : 'Bật'}
              </Box>
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tổng sản phẩm"
            value={statistics.totalProducts}
            icon={<InventoryIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Xu hướng giá"
            value={statistics.averageCostTrend === 'stable' ? 'Ổn định' : 'Biến động'}
            icon={<TrendingUpIcon />}
            trend={statistics.averageCostTrend === 'up' ? 'up' : null}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cập nhật gần nhất"
            value={statistics.lastUpdateDate || '-'}
            icon={<UpdateIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Thay đổi gần đây"
            value={statistics.recentCostChanges}
            icon={<AttachMoneyIcon />}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper 
        sx={{ 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(135deg, #0e2a04 0%, #1a4f0a 100%)',
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 64,
              transition: 'all 0.2s ease',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              color: '#475569',
              '&:hover': {
                color: '#0e2a04',
                transform: 'translateY(-1px)'
              },
              '&.Mui-selected': {
                background: 'rgba(14, 42, 4, 0.05)',
                color: '#0e2a04'
              }
            }
          }}
        >
          <Tab label="Tổng quan" />
          <Tab label="Sản phẩm & Giá cả" />
          <Tab label="Thông tin liên hệ" />
          <Tab label="Tài chính" icon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    fontWeight: 600,
                    color: '#1a1a1a',
                    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  <BusinessIcon sx={{ mr: 1, color: '#0e2a04' }} />
                  Thông tin cơ bản
                </Typography>
                <InfoItem icon={<BusinessIcon />} label="Tên nhà cung cấp" value={supplier.name} />
                <InfoItem icon={<LocationOnIcon />} label="Địa chỉ" value={supplier.address} />
                <InfoItem icon={<PhoneIcon />} label="Số điện thoại" value={supplier.phone} />
                <InfoItem icon={<EmailIcon />} label="Email" value={supplier.email} />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e0e0e0',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    fontWeight: 600,
                    color: '#1a1a1a',
                    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  <PersonIcon sx={{ mr: 1, color: '#0e2a04' }} />
                  Thông tin liên hệ
                </Typography>
                <InfoItem icon={<PersonIcon />} label="Tên người liên hệ" value={supplier.primary_contact_name} />
                <InfoItem icon={<EmailIcon />} label="Email người liên hệ" value={supplier.primary_contact_email} />
                <InfoItem icon={<PhoneIcon />} label="Điện thoại người liên hệ" value={supplier.primary_contact_phone} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card
          sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Add Product Section */}
            <Box 
              sx={{ 
                mb: 0, 
                p: 4, 
                background: '#f4f1e8',
                borderBottom: '1px solid #F1F5F9'
              }}
            >
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#1E293B',
                  fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  mb: 3
                }}
              >
                Thêm sản phẩm mới
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2, 
                alignItems: { xs: 'stretch', sm: 'center' }
              }}>
                <Autocomplete
                  size="small"
                  sx={{ 
                    flex: 1, 
                    maxWidth: { xs: '100%', sm: 400 },
                    minWidth: { xs: '100%', sm: 250 }
                  }}
                  options={availableProducts}
                  getOptionLabel={(option) => option.name}
                  value={selectedProduct}
                  onChange={(event, newValue) => setSelectedProduct(newValue)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Chọn sản phẩm" 
                      placeholder="Tìm kiếm sản phẩm..."
                    />
                  )}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProduct}
                  disabled={!selectedProduct || addingProduct}
                  sx={{
                    background: 'linear-gradient(135deg, #0e2a04 0%, #1a4f0a 100%)',
                    borderRadius: '12px',
                    px: { xs: 3, sm: 4 },
                    py: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                    boxShadow: '0 4px 14px rgba(14, 42, 4, 0.25)',
                    border: 'none',
                    minWidth: { xs: '100px', sm: 'auto' },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1a4f0a 0%, #0e2a04 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(14, 42, 4, 0.4)'
                    },
                    '&:disabled': {
                      background: 'rgba(244, 241, 232, 0.8)',
                      color: '#8b7355',
                      boxShadow: 'none',
                      backdropFilter: 'blur(10px)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Thêm
                </Button>
              </Box>
            </Box>

            {/* Products Table */}
            <Box sx={{ p: 4 }}>
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography 
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1a1a1a',
                    fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  Danh sách sản phẩm ({supplier.products?.items?.length || 0})
                </Typography>
                <TextField
                  size="small"
                  placeholder="Tìm kiếm sản phẩm..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      border: '2px solid #e0e0e0',
                      fontSize: '16px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor: '#ffffff',
                      '&:hover fieldset': {
                        borderColor: '#6b8e4e',
                        boxShadow: '0 0 0 2px rgba(107, 142, 78, 0.1)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0e2a04',
                        boxShadow: '0 0 0 4px rgba(14, 42, 4, 0.15)',
                        outline: 'none'
                      },
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                        transition: 'all 0.2s ease'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: '#1a1a1a',
                      '&::placeholder': {
                        color: '#666666',
                        opacity: 1
                      }
                    }
                  }}
                />
              </Box>

            {supplier.products && supplier.products.items.length > 0 ? (
              <TableContainer 
                component={Paper} 
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(14, 42, 4, 0.1)',
                  overflow: 'auto',
                  maxWidth: '100%',
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#0e2a04',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#1a4f0a',
                    }
                  }
                }}
              >
                <Table sx={{ minWidth: { xs: 600, sm: 750 } }}>
                  <TableHead>
                    <TableRow 
                      sx={{
                        background: '#f4f1e8',
                        '& .MuiTableCell-head': {
                          fontWeight: 600,
                          color: '#1a1a1a',
                          borderBottom: '2px solid rgba(14, 42, 4, 0.1)',
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segue UI', system-ui, sans-serif",
                          py: 2,
                          fontSize: { xs: '0.875rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap'
                        }
                      }}
                    >
                      <TableCell sx={{ width: { xs: '60px', sm: '80px' } }}>ID</TableCell>
                      <TableCell sx={{ minWidth: { xs: '150px', sm: '200px' } }}>Tên sản phẩm</TableCell>
                      <TableCell sx={{ minWidth: { xs: '120px', sm: '140px' } }}>Giá hiện tại</TableCell>
                      <TableCell sx={{ minWidth: { xs: '120px', sm: '140px' } }}>Cập nhật lần cuối</TableCell>
                      <TableCell align="right" sx={{ minWidth: { xs: '150px', sm: '180px' } }}>Thao tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplier.products.items
                      .filter(item => 
                        item && item.product && (
                          searchTerm === '' || 
                          item.product.name.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                      )
                      .map((supplierProduct) => (
                        <ProductCostRow
                          key={supplierProduct.id}
                          supplierProduct={supplierProduct}
                          product={supplierProduct.product}
                        />
                      ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Paper 
                sx={{ 
                  p: 8, 
                  textAlign: 'center',
                  borderRadius: '16px',
                  border: '2px dashed rgba(14, 42, 4, 0.3)',
                  background: 'linear-gradient(135deg, rgba(14, 42, 4, 0.02) 0%, rgba(14, 42, 4, 0.05) 100%)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Box 
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, rgba(14, 42, 4, 0.1) 0%, rgba(14, 42, 4, 0.2) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <InventoryIcon sx={{ fontSize: 48, color: '#0e2a04' }} />
                </Box>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: '#1a1a1a',
                    mb: 2
                  }}
                >
                  Chưa có sản phẩm nào
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#666666',
                    fontSize: '1.1rem',
                    maxWidth: 400,
                    mx: 'auto'
                  }}
                >
                  Thêm sản phẩm để bắt đầu quản lý giá cả và theo dõi chi phí nhà cung cấp
                </Typography>
              </Paper>
            )}
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card
          sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 4,
                fontWeight: 600,
                color: '#1E293B',
                fontFamily: "'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              <PersonIcon sx={{ mr: 1, color: '#0e2a04' }} />
              Thông tin liên hệ chi tiết
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <InfoItem icon={<PersonIcon />} label="Tên người liên hệ" value={supplier.primary_contact_name} />
                <InfoItem icon={<EmailIcon />} label="Email người liên hệ" value={supplier.primary_contact_email} />
                <InfoItem icon={<PhoneIcon />} label="Điện thoại người liên hệ" value={supplier.primary_contact_phone} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoItem icon={<BusinessIcon />} label="Tên công ty" value={supplier.name} />
                <InfoItem icon={<PhoneIcon />} label="Số điện thoại công ty" value={supplier.phone} />
                <InfoItem icon={<EmailIcon />} label="Email công ty" value={supplier.email} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 3 && (
        <SupplierFinanceTab
          supplierId={supplier.id}
          onPaymentComplete={() => setForceRerender(prev => !prev)}
        />
      )}
    </Container>
  );
};

export default SupplierDetail;